import { Button } from '@/components/common';
import { MESSAGE_TYPES } from '@/constants';
import { ReferidoNavigator } from './ReferidoNavigator';
import type { ReferidoFormData, MessageType } from '@/types';

interface PhaseMessagesProps {
  referidos: ReferidoFormData[];
  currentIndex: number;
  onUpdateReferido: (index: number, updates: Partial<ReferidoFormData>) => void;
  onNavigate: (index: number) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function PhaseMessages({
  referidos,
  currentIndex,
  onUpdateReferido,
  onNavigate,
  onContinue,
  onBack,
}: PhaseMessagesProps) {
  const current = referidos[currentIndex];

  if (!current) return null;

  const filledCount = referidos.filter((r) => r.mensaje_enviado).length;
  const allFilled = filledCount === referidos.length;

  const displayName = current.nombre + (current.apellido ? ` ${current.apellido}` : '');

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Envía el mensaje
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Selecciona por qué medio enviaste el mensaje a cada referido.{' '}
          {filledCount}/{referidos.length} completados
        </p>
      </div>

      {/* Navigator */}
      <ReferidoNavigator
        currentIndex={currentIndex}
        total={referidos.length}
        onPrev={() => onNavigate(currentIndex - 1)}
        onNext={() => onNavigate(currentIndex + 1)}
        currentName={displayName}
      />

      {/* Message type selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          ¿Por qué medio enviaste el mensaje a{' '}
          <span className="text-primary-600 font-semibold">{current.nombre}</span>?
        </label>
        <div className="grid grid-cols-2 gap-2">
          {MESSAGE_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() =>
                onUpdateReferido(currentIndex, { mensaje_enviado: type.value as MessageType })
              }
              className={`px-3 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                current.mensaje_enviado === type.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mini list showing all referidos status */}
      <div className="space-y-1">
        {referidos.map((ref, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onNavigate(idx)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
              idx === currentIndex
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'hover:bg-gray-50 text-gray-600'
            }`}
          >
            <span>{ref.nombre}{ref.apellido ? ` ${ref.apellido}` : ''}</span>
            {ref.mensaje_enviado ? (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                {ref.mensaje_enviado === 'WHATSAPP'
                  ? 'WhatsApp'
                  : ref.mensaje_enviado === 'IMESSAGE'
                  ? 'iMessage'
                  : ref.mensaje_enviado === 'CITA_INSTANTANEA'
                  ? 'Cita'
                  : 'SMS'}
              </span>
            ) : (
              <span className="text-xs text-gray-400">pendiente</span>
            )}
          </button>
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-3 pt-2">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          Volver
        </Button>
        {allFilled ? (
          <Button onClick={onContinue} className="flex-1">
            Continuar a Teléfonos
          </Button>
        ) : (
          <Button variant="outline" onClick={onContinue} className="flex-1">
            Continuar ({filledCount}/{referidos.length})
          </Button>
        )}
      </div>
    </div>
  );
}
